import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2, CreditCard, FileText, Loader2, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOC_CONFIG = [
  { key: 'dni_front', label: 'DNI Frente', Icon: CreditCard, accept: 'image/*', type: 'image', testId: 'reupload-dni-front' },
  { key: 'dni_back', label: 'DNI Dorso', Icon: CreditCard, accept: 'image/*', type: 'image', testId: 'reupload-dni-back' },
  { key: 'criminal_record', label: 'Antecedentes Penales', Icon: FileText, accept: 'application/pdf', type: 'pdf', testId: 'reupload-criminal-record' },
];

function DocRow({ label, Icon, accept, file, onFileSelect, testId }) {
  const done = Boolean(file);
  return (
    <div className={`flex items-center gap-3 rounded-xl p-3.5 border transition-all ${done ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-white'}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${done ? 'bg-green-100' : 'bg-gray-100'}`}>
        {done ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <Icon className="w-5 h-5 text-gray-400" />}
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-sm font-semibold text-gray-900">{label}</span>
        {done ? <p className="text-xs text-green-600 mt-0.5 truncate">{file.name}</p> : null}
      </div>
      <label className={`flex-shrink-0 cursor-pointer px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${done ? 'border-green-400 text-green-700 hover:bg-green-100' : 'border-orange-400 text-orange-600 hover:bg-orange-50'}`}>
        {done ? 'Cambiar' : 'Seleccionar'}
        <input type="file" accept={accept} onChange={onFileSelect} className="hidden" data-testid={testId} />
      </label>
    </div>
  );
}

export default function ReuploadDocumentsModal({ open, onClose, onSuccess, token }) {
  const [docs, setDocs] = useState({ dni_front: null, dni_back: null, criminal_record: null });
  const [submitting, setSubmitting] = useState(false);

  const handleFileChange = (e, docKey, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (type === 'image') {
      if (!file.type.startsWith('image/')) { toast.error('Debe ser una imagen (JPG, PNG, WEBP)'); e.target.value = ''; return; }
      if (file.size > 10 * 1024 * 1024) { toast.error('La imagen no puede superar los 10MB'); e.target.value = ''; return; }
    }
    if (type === 'pdf') {
      if (file.type !== 'application/pdf') { toast.error('Debe ser un archivo PDF'); e.target.value = ''; return; }
      if (file.size > 15 * 1024 * 1024) { toast.error('El PDF no puede superar los 15MB'); e.target.value = ''; return; }
    }
    setDocs((prev) => ({ ...prev, [docKey]: file }));
  };

  const allSelected = docs.dni_front && docs.dni_back && docs.criminal_record;

  const handleSubmit = async () => {
    if (!allSelected) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('dni_front', docs.dni_front);
      formData.append('dni_back', docs.dni_back);
      formData.append('criminal_record', docs.criminal_record);
      await axios.post(`${API}/providers/documents`, formData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('¡Documentos actualizados! Ya podés ver los datos de contacto.');
      setDocs({ dni_front: null, dni_back: null, criminal_record: null });
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No pudimos subir los documentos. Intentá de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent data-testid="reupload-documents-modal" className="max-w-md max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-2" style={{ backgroundColor: 'var(--primary-light)' }}>
            <ShieldCheck className="w-6 h-6" style={{ color: 'var(--primary-strong)' }} />
          </div>
          <DialogTitle>Necesitamos verificar tu identidad de nuevo</DialogTitle>
          <DialogDescription className="leading-relaxed">
            Por una actualización en nuestros sistemas, perdimos una copia de tus documentos de verificación.
            Te pedimos que los subas una única vez para poder seguir mostrándote los datos de contacto de los clientes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2.5 py-2 min-w-0">
          {DOC_CONFIG.map(({ key, label, Icon, accept, type, testId }) => (
            <DocRow
              key={key}
              label={label}
              Icon={Icon}
              accept={accept}
              file={docs[key]}
              testId={testId}
              onFileSelect={(e) => handleFileChange(e, key, type)}
            />
          ))}
        </div>

        <DialogFooter className="gap-2">
          <Button data-testid="reupload-skip-btn" variant="outline" onClick={() => onClose?.()} disabled={submitting}>
            Ahora no
          </Button>
          <Button
            data-testid="reupload-submit-btn"
            onClick={handleSubmit}
            disabled={!allSelected || submitting}
            style={{ backgroundColor: 'var(--primary-strong)' }}
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Subir documentos
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
